import { z } from "zod";
import { helpTopicIds } from "./helpTopics.js";
export { helpTopicIds, helpTopics, helpDocumentTopics, resolveHelpTopic, type HelpTopicId, type HelpTopicSummary } from "./helpTopics.js";

export const helpAudiences = ["students", "parents", "families", "tutors"] as const;
export const helpScopes = ["all", "private", "school"] as const;
export const helpAudienceLabels = { students: "Students", parents: "Parents", families: "Students and parents", tutors: "Tutors" };
export const helpScopeLabels = { all: "All lessons", private: "Private lessons", school: "School lessons" };
export const helpScreenshotViews = ["student", "parent", "tutor"] as const;
export type HelpScreenshotView = typeof helpScreenshotViews[number];
export const helpScreenshotViewLabels = { student: "Student account", parent: "Parent account", tutor: "Tutor account" };
export function helpScreenshotViewsForAudience(audience: typeof helpAudiences[number]): HelpScreenshotView[] {
  return audience === "tutors" ? ["tutor"] : ["student", "parent"];
}
/** Family accounts never inherit a staff screenshot, including in shared guides. */
export function helpScreenshotViewsForReader(audience: typeof helpAudiences[number], roles: readonly string[] | null): HelpScreenshotView[] {
  const staff = roles?.some(role => role === "owner" || role === "tutor");
  if (audience === "tutors") return staff ? ["tutor"] : [];
  return !roles || staff ? ["student", "parent"] : helpScreenshotViewsForAudience(audience).filter(view => roles.includes(view));
}
export const helpSlug = z.string().min(3).max(100).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
  .refine(value => !["manage", "images", "new"].includes(value), "Choose another address.");
export function helpSlugFromTitle(title: string): string {
  const slug = title.normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[’']/g, "")
    .replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 100).replace(/-$/, "");
  return helpSlug.safeParse(slug).success ? slug : `guide${slug ? `-${slug}` : ""}`.slice(0, 100).replace(/-$/, "");
}
const paragraph = z.string().trim().max(6000);
const draftScreenshot = z.object({ imageId: z.string().uuid(), imageAlt: z.string().trim().max(300) }).strict();
const screenshot = draftScreenshot.extend({ imageAlt: draftScreenshot.shape.imageAlt.min(1, "Describe the screenshot for readers who cannot see it.") });
const draftStep = z.object({
  text: paragraph,
  screenshots: z.object({ student: draftScreenshot.optional(), parent: draftScreenshot.optional(), tutor: draftScreenshot.optional() }).strict().optional(),
}).strict();
const topicSelection = z.array(z.enum(helpTopicIds)).max(helpTopicIds.length)
  .refine(topics => new Set(topics).size === topics.length, "Choose each topic once.");
const documentFields = z.object({
  title: z.string().trim().max(160),
  summary: z.string().trim().max(600),
  audience: z.enum(helpAudiences),
  scope: z.enum(helpScopes),
  category: z.string().trim().max(60),
  topics: topicSelection.optional(),
  keywords: z.string().trim().max(500),
  public: z.boolean(),
  before: paragraph,
  steps: z.array(draftStep).max(30),
  afterwards: paragraph,
  troubleshooting: paragraph,
}).strict();
/** Drafts may be unfinished, but keep the same bounds, account rules and image references. */
export const helpDraftDocumentSchema = documentFields.refine(document => document.audience !== "tutors" || !document.public, "Tutor guides cannot be public.")
  .refine(document => document.steps.every(step => Object.keys(step.screenshots ?? {}).every(view =>
    helpScreenshotViewsForAudience(document.audience).includes(view as HelpScreenshotView))), "Use screenshots from the accounts this guide is for.");
export const helpDocumentSchema = documentFields.extend({
  title: documentFields.shape.title.min(3, "Add a title of at least 3 characters."),
  summary: documentFields.shape.summary.min(10, "Add a short explanation of at least 10 characters."),
  category: documentFields.shape.category.min(2, "Add a category."),
  topics: topicSelection.refine(topics => topics.length > 0, "Choose at least one topic.").optional(),
  steps: z.array(draftStep.extend({ text: paragraph.min(1, "Write this step."),
    screenshots: z.object({ student: screenshot.optional(), parent: screenshot.optional(), tutor: screenshot.optional() }).strict().optional(),
  })).min(1, "Add at least one step.").max(30),
  afterwards: paragraph.min(1, "Describe what readers should see afterwards."),
}).refine(document => document.audience !== "tutors" || !document.public, "Tutor guides cannot be public.")
  .refine(document => document.steps.every(step => Object.keys(step.screenshots ?? {}).every(view =>
    helpScreenshotViewsForAudience(document.audience).includes(view as HelpScreenshotView))), "Use screenshots from the accounts this guide is for.");

export type HelpDocument = z.infer<typeof helpDocumentSchema>;
export interface HelpArticle {
  id: string;
  slug: string;
  document: HelpDocument;
  publishedAt: string | null;
}
export interface HelpDraft extends HelpArticle {
  version: number;
  linkLocked: boolean;
  published: HelpDocument | null;
  updatedAt: string;
}
export interface HelpRevision {
  id: string;
  version: number;
  action: "create" | "save" | "publish" | "unpublish" | "restore";
  createdAt: string;
  document: HelpDocument;
}
export function helpArticlePath(slug: string): string { return `/help/${encodeURIComponent(slug)}`; }
